import {
  ABOUT_MOMENT,
  HELP_TEXT,
  WHOAMI_TEXT,
  DISCLOSURES,
} from './challenge-spec';
import { animations, type AnimationName } from './ascii-animations';

export type CommandResult = {
  lines: string[];
  type: 'output' | 'error' | 'amber' | 'cyan' | 'system';
  special?: 'clear' | 'pong' | 'adventure' | 'swarm' | 'restart';
};

const ANIMATION_NAMES = Object.keys(animations) as AnimationName[];
const INIT_STORAGE_KEY = 'moment-boot-animation';

export function getBootAnimation(): AnimationName {
  if (typeof window === 'undefined') return 'interference';
  return (localStorage.getItem(INIT_STORAGE_KEY) as AnimationName) || 'interference';
}

function setBootAnimation(name: AnimationName) {
  localStorage.setItem(INIT_STORAGE_KEY, name);
}

const THEME_NAMES = ['default', 'paper', 'mono', 'pacman', 'sepia', 'bauhaus'] as const;
export type ThemeName = (typeof THEME_NAMES)[number];
const THEME_STORAGE_KEY = 'moment-theme';

export function getTheme(): ThemeName {
  if (typeof window === 'undefined') return 'default';
  return (localStorage.getItem(THEME_STORAGE_KEY) as ThemeName) || 'default';
}

export function applyTheme(name: ThemeName) {
  localStorage.setItem(THEME_STORAGE_KEY, name);
  if (name === 'default') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', name);
  }
}

function handleThemeCommand(cmd: string): CommandResult | null {
  if (cmd === 'theme') {
    return {
      lines: [
        '',
        '  Usage: theme set <name>',
        '',
        '  Change the terminal color theme.',
        '  Type "theme set" to see available themes.',
        '',
      ],
      type: 'output',
    };
  }

  if (cmd === 'theme set' || cmd === 'theme -h') {
    const current = getTheme();
    return {
      lines: [
        '',
        '  Available themes:',
        '',
        ...THEME_NAMES.map(
          (name) => `    ${name === current ? '→' : ' '} ${name}${name === current ? '  (current)' : ''}`
        ),
        '',
        '  Usage: theme set <name>',
        '',
      ],
      type: 'output',
    };
  }

  const setMatch = cmd.match(/^theme set (\S+)$/);
  if (setMatch) {
    const name = setMatch[1] as string;
    if (THEME_NAMES.includes(name as ThemeName)) {
      applyTheme(name as ThemeName);
      return {
        lines: [
          '',
          `  Theme set to "${name}".`,
          '',
        ],
        type: 'amber',
      };
    }
    return {
      lines: [
        '',
        `  Unknown theme: "${name}"`,
        '',
        '  Available: ' + THEME_NAMES.join(', '),
        '',
      ],
      type: 'error',
    };
  }

  return null;
}

function handleInitializeCommand(cmd: string): CommandResult | null {
  if (cmd === 'initialize') {
    return {
      lines: [
        '',
        '  Usage: initialize set <animation>',
        '',
        '  Configure the boot sequence animation.',
        '  Type "initialize -h" to see available animations.',
        '',
      ],
      type: 'output',
    };
  }

  if (cmd === 'initialize -h' || cmd === 'initialize set') {
    const current = getBootAnimation();
    return {
      lines: [
        '',
        '  Available animations:',
        '',
        ...ANIMATION_NAMES.map(
          (name) => `    ${name === current ? '→' : ' '} ${name}${name === current ? '  (current)' : ''}`
        ),
        '',
        '  Usage: /initialize set <animation>',
        '',
      ],
      type: 'output',
    };
  }

  const setMatch = cmd.match(/^initialize set (\S+)$/);
  if (setMatch) {
    const name = setMatch[1] as string;
    if (ANIMATION_NAMES.includes(name as AnimationName)) {
      setBootAnimation(name as AnimationName);
      return {
        lines: [
          '',
          `  Boot animation set to "${name}".`,
          '  It will appear on your next initialization.',
          '',
        ],
        type: 'amber',
      };
    }
    return {
      lines: [
        '',
        `  Unknown animation: "${name}"`,
        '',
        '  Available: ' + ANIMATION_NAMES.join(', '),
        '',
      ],
      type: 'error',
    };
  }

  return null;
}

export function executeCommand(input: string): CommandResult {
  const cmd = input.trim().toLowerCase();

  const themeResult = handleThemeCommand(cmd);
  if (themeResult) return themeResult;

  const initResult = handleInitializeCommand(cmd);
  if (initResult) return initResult;

  switch (cmd) {
    case 'help':
    case 'h':
    case '?':
      return { lines: HELP_TEXT, type: 'output' };

    case 'about':
    case 'moment':
      return { lines: ABOUT_MOMENT, type: 'output' };

    case 'clear':
    case 'cls':
      return { lines: [], type: 'output', special: 'clear' };

    case 'adventure':
    case 'dungeon':
    case 'nexus':
      return {
        lines: [
          '',
          'NEXUS TERMINAL v4.0',
          '\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          'WARNING: System degradation detected at 02:41 UTC.',
          'P&L impact: -$50,000/min and rising.',
          'Connecting to NEXUS debug interface...',
          'Connected. Type "quit" to disconnect.',
          '',
        ],
        type: 'cyan',
        special: 'adventure',
      };

    case 'swarm':
    case 'colony':
    case 'ants':
      return {
        lines: [
          '',
          '  SWARM ENVIRONMENT v1.0',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '  Colony initialized. 200 ants standing by.',
          '  Type "help" for swarm commands. "quit" to exit.',
          '',
        ],
        type: 'cyan',
        special: 'swarm',
      };

    case 'game':
    case 'pong':
    case 'play':
      return {
        lines: ['', '  Loading PONG...', ''],
        type: 'cyan',
        special: 'pong',
      };

    case 'disclosures':
    case 'disclosure':
    case 'legal':
      return { lines: DISCLOSURES, type: 'output' };

    case 'restart':
    case 'reboot':
      return {
        lines: ['', '  Rebooting...', ''],
        type: 'amber',
        special: 'restart',
      };

    case 'whoami':
      return { lines: WHOAMI_TEXT, type: 'cyan' };

    case 'sudo':
    case 'sudo rm -rf /':
      return {
        lines: ['', '  Nice try.', ''],
        type: 'error',
      };

    case 'exit':
    case 'quit':
      return {
        lines: ['', '  There is no escape. Only the challenge.', ''],
        type: 'amber',
      };

    case '':
      return { lines: [], type: 'output' };

    default:
      return {
        lines: [
          '',
          `  Unknown command: ${cmd}`,
          '  Type "help" for available commands.',
          '',
        ],
        type: 'error',
      };
  }
}
